import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import type { BjBonusStatus, RlChatMessage, RlGameUpdate, RlPlaceKind } from "@ziolo/shared";

const EMPTY_TABLE: RlGameUpdate = {
  phase: "betting",
  roundNumber: 0,
  players: [],
  bets: [],
  lastWinningNumbers: [],
  winningNumber: null,
  bettingEndsAt: null,
  spinEndsAt: null,
  resultEndsAt: null,
  winners: [],
};

export interface RuletkaSession extends BjBonusStatus {
  seated: boolean;
}

export interface SpinCue {
  winningNumber: number;
  spinEndsAt: number;
  roundNumber: number;
}

export function useRuletkaSocket(enabled: boolean) {
  const socketRef = useRef<Socket | null>(null);
  const [table, setTable] = useState<RlGameUpdate>(EMPTY_TABLE);
  const [session, setSession] = useState<RuletkaSession | null>(null);
  const [chat, setChat] = useState<RlChatMessage[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletEpoch, setWalletEpoch] = useState(0);
  const [spin, setSpin] = useState<SpinCue | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const socket = io(`${window.location.origin}/ruletka`, {
      path: "/socket.io/",
      withCredentials: true,
      transports: ["polling", "websocket"],
      forceNew: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setError(null);
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", (err: Error) => {
      setConnected(false);
      setError(
        err.message === "UNAUTHORIZED"
          ? "Sesja wygasła. Zaloguj się ponownie."
          : "Nie udało się połączyć ze stołem. Odśwież stronę.",
      );
    });
    socket.on("game_update", (update: RlGameUpdate) => setTable(update));
    socket.on("session", (next: RuletkaSession) => setSession(next));
    socket.on("spin", (cue: SpinCue) => setSpin(cue));
    socket.on("chat:init", (payload: { messages: RlChatMessage[] }) => {
      setChat(payload.messages);
    });
    socket.on("chat:new", (message: RlChatMessage) => {
      setChat((prev) => [...prev, message].slice(-150));
    });
    socket.on("daily:status", (status: BjBonusStatus) => {
      setSession((prev) => ({ seated: prev?.seated ?? false, ...status }));
    });
    socket.on("daily:claimed", (result: { ok: boolean; chips?: number; nextAvailableAt?: number | null }) => {
      if (result.ok) {
        setWalletEpoch((n) => n + 1);
        setSession((prev) => ({
          seated: prev?.seated ?? false,
          canClaim: false,
          nextAvailableAt: result.nextAvailableAt ?? Date.now() + 2 * 60 * 60 * 1000,
          amount: 2137,
          chips: result.chips ?? prev?.chips ?? 0,
          keys: prev?.keys ?? 0,
          equippedSkin: prev?.equippedSkin ?? "classic",
        }));
      }
    });
    socket.on("notice", (payload: { text: string }) => {
      setNotice(payload.text);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  const emit = useCallback((event: string, payload?: unknown) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (payload === undefined) socket.emit(event);
    else socket.emit(event, payload);
  }, []);

  return {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    spin,
    join: () => emit("join_game"),
    leave: () => emit("leave_game"),
    placeBet: (kind: RlPlaceKind, amount: number, numbers: number[] = []) =>
      emit("place_bet", { kind, amount, numbers }),
    clearBets: () => emit("clear_bets"),
    sendChat: (text: string) => emit("chat:send", { text, cid: crypto.randomUUID() }),
    claimBonus: () => emit("daily:claim"),
  };
}
